<template>
  <div class="history">
    <v-base-card>
      <template v-slot:heading>
        学校別申し込み状況
      </template>

      <template v-slot:content>
        <v-row class="mb-0 pb-0">
          <v-col cols="2">
            <v-text-field
              v-model="schoolCode"
              label="学校コード"
              color="secondary"
              class="schoolcode-width"
              hide-details
            />
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="selectedPrefectureCode"
              :items="prefectures"
              item-text="name"
              item-value="code"
              label="都道府県"
              clearable
              class="prefectres-width"
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="6">
            <div class="wrapper">
              <v-menu
                ref="startMenu"
                v-model="startMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDateText"
                    label="PW発行日・更新日-開始-"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                />
              </v-menu>
              <v-menu
                ref="endMenu"
                v-model="endMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDateText"
                    label="PW発行日・更新日-終了-"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                />
              </v-menu>
            </div>
          </v-col>
          <v-col cols="1" class="d-flex align-center">
            <v-btn small color="secondary" @click="search">検索</v-btn>
          </v-col>
          <v-col cols="1" class="d-flex">
            <v-btn class="mt-5 ml-auto" small color="#4a81aa" dark @click.stop="downloadCsv">csvダウンロード</v-btn>
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="schools"
          :loading="loading"
          loading-text="Loading... Please wait"
          hide-default-footer
          :items-per-page="-1"
          :height="300"
        >
          <template #[`item.applications`]="{ item }">
            <v-btn small color="secondary" rounded dark @click.stop="showApplications(item)">確認</v-btn>
          </template>
        </v-data-table>
        <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" color="secondary" @input="onPageChange" />
      </template>
    </v-base-card>
  </div>
</template>

<script>
import VBaseCard from '@/components/molecules/BaseCard'
import { saveAs } from 'file-saver'

export default {
  components: {
    VBaseCard,
  },
  data () {
    return {
      headers: [
        { text: '学校名', align: 'start', value: 'name' },
        { text: '学校コード', align: 'start', value: 'code' },
        { text: '都道府県', align: 'center', value: 'prefecture_name'},
        { text: '未処理件数', align: 'center', value: 'untreated_count'},
        { text: '内容確認中件数', align: 'center', value: 'processingd_count'},
        { text: '処理済み件数', align: 'center', value: 'treated_count'},
        { text: '申し込み一覧', align: 'center', value: 'applications', sortable: false },
      ],
      schools: [],
      selectedPrefectureCode: null,
      loading: false,
      currentPage: 1,
      totalPages: 0,
      schoolCode: null,
      startDate: null,
      startMenu: false,
      endDate: null,
      endMenu: false,
    }
  },

  async mounted() {
    await this.getSchools()
    this.$store.commit('sheet/setSheetType', { sheetType: null })
  },

  computed: {
    prefectures() {
      return this.$store.state.master.allPrefectures
    },
    startDateText: {
      get() {
        return this.formatDate(this.startDate)
      },
      set() {
        this.startDate = null
      }
    },
    endDateText: {
      get() {
        return this.formatDate(this.endDate)
      },
      set() {
        this.endDate = null
      }
    },
  },

  methods: {
    async getSchools() {
      this.loading = true
      const params = new URLSearchParams()
      params.append('page', this.currentPage)
      params.append('q[with_requests][]', 'untreated')
      params.append('q[with_requests][]', 'processing')
      if (this.schoolCode) params.append('q[code_cont]', this.schoolCode)
      if (this.selectedPrefectureCode) params.append('q[prefecture_code_eq]', this.selectedPrefectureCode)
      const res = await this.$axios(`/api/v1/admin/schools?${params}`)
      this.schools = res.data.items
      this.totalPages = res.data.total_pages
      this.loading = false
    },
    search() {
      this.currentPage = 1
      this.getSchools()
    },
    onPageChange() {
      this.getSchools()
    },
    // 先生_お知らせCSVダウンロードAPI
    async downloadCsv() {
      this.$app.$refs.loading.open({message: 'CSVダウンロード中'})
      const params = []

      // 学校コードの条件
      if (this.schoolCode) {
        params.push(`q[school_code_cont]=${this.schoolCode}`)
      }

      // 都道府県の条件
      const prefecture_code = this.selectedPrefectureCode
        ? this.prefectures.find(pref => pref.code === this.selectedPrefectureCode).code
        : ''
      params.push(`q[school_prefecture_code_eq]=${prefecture_code}`)

      // 開始日（開始日以降 → gteq）
      if (this.startDate) {
        const startOfDay = `${this.startDateText} 00:00:00`
        params.push(`q[subject_teachers_user_changed_password_at_gteq]=${encodeURIComponent(startOfDay)}`)
      }

      // 終了日（終了日以前 → lteq）
      if (this.endDate) {
        const endOfDay = `${this.endDateText} 23:59:59`
        params.push(`q[subject_teachers_user_changed_password_at_lteq]=${encodeURIComponent(endOfDay)}`)
      }

      // 申し込みの状態条件
      params.push(`q[with_requests][]=untreated`)
      params.push(`q[with_requests][]=processing`)

      const res = await this.$axios(`/api/v1/admin/schools/teachers.csv/?${params.join('&')}`,{responseType: 'blob'})
      const blob = new Blob([res.data], { type: res.data.type })

      //レスポンスヘッダからファイル名を取得
      const contentDisposition = res.headers['content-disposition']
      const fileName = this.getFileName(contentDisposition)

      saveAs(blob, fileName)
      this.$app.$refs.loading.close({})
    },
    getFileName(contentDisposition){
      let fileName = contentDisposition.substring(contentDisposition.indexOf("''") + 2,
                                                    contentDisposition.length
                                                 )
      //デコードするとスペースが"+"になるのでスペースへ置換
      fileName = decodeURI(fileName).replace(/\+/g, ' ')

      return fileName
    },
    showApplications(school) {
      this.$store.commit('school/setSchool', school)
      this.$router.push(`/admin/applications/${school.id}`)
    },
    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${year}/${month}/${day}`
    },
  }
}
</script>

<style lang="stylus" scoped>

.select
  font-size 11px !important
.csv_upload
  font-size 14px

.schoolcode-width
  min-width 100px //学校コード入力エリアの最小幅固定
.prefectres-width
  min-width 128px //都道府県選択エリアの最小幅固定

.wrapper
  display: flex
  justify-content: space-between
  align-items: center
  gap: 20px
</style>
